import { useEffect, useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Collapse,
  Descriptions,
  Drawer,
  Input,
  Radio,
  Segmented,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { ReloadOutlined } from '@ant-design/icons'
import { supabase } from '@/lib/supabase'
import { formatCST } from '@/lib/time'
import { AdminMobileList } from '@/components/AdminMobileList'
import { AdminRecordActions } from '@/components/AdminRecordActions'
import { useIsMobile } from '@/lib/hooks'
import { ReplyTemplatePicker } from '@/components/ReplyTemplatePicker'
import {
  EXAM_DECISION_META,
  formatSimilarity,
  parseExamDedup,
  refKeyLabel,
  similarityBreakdown,
  similarityColor,
  type ExamDedup,
} from '@/lib/content-dedup'

export const Route = createFileRoute('/_authed/exam-contributions')({
  component: ExamContributionsPage,
})

/* ── 类型 ───────────────────────────────────────────────────── */

type SubmissionStatus =
  | 'pending'
  | 'needs_changes'
  | 'rejected'
  | 'accepted'
  | 'published'
  | 'withdrawn'
type ReviewOutcome = 'accepted' | 'needs_changes' | 'rejected'

interface ExamOption {
  key: string
  text: string
}

interface ExamSubmissionRow {
  id: string
  status: SubmissionStatus
  body: string
  options: ExamOption[]
  correct_option: string
  explanation: string
  submitter_note: string | null
  show_contributor: boolean
  user_id: string | null
  version: number
  review_note: string | null
  reviewed_at: string | null
  reviewed_by: string | null
  exam_question_id: number | null
  published_at: string | null
  created_at: string
  updated_at: string
  submitter_display?: string | null
  submitter_username?: string | null
  reviewer_display?: string | null
  reviewer_username?: string | null
}

interface Revision {
  version: number
  snapshot: {
    body?: string
    options?: ExamOption[]
    correct_option?: string
    explanation?: string
    submitter_note?: string | null
    status?: SubmissionStatus
    review_note?: string | null
    version?: number
    created_at?: string
  }
  created_at: string
}

interface DetailResult {
  ok: boolean
  submission?: ExamSubmissionRow
  revisions?: Revision[]
  /** 语义查重：flag = 提交时的标记（无则 null），similar = 实时相似（题库 + 投稿，排除自身） */
  dedup?: unknown
  error?: string
}

/** 审核时可选的整体替换内容（RPC 校验恰好这 4 个键，绝不含作者备注） */
interface EditContent {
  body: string
  options: ExamOption[]
  correct_option: string
  explanation: string
}

/* ── i18n / 状态元数据 ──────────────────────────────────────── */

const STATUS_META: Record<SubmissionStatus, { label: string; color: string }> =
  {
    pending: { label: '待审', color: 'warning' },
    needs_changes: { label: '需修改', color: 'purple' },
    accepted: { label: '已采纳', color: 'success' },
    rejected: { label: '未采纳', color: 'volcano' },
    published: { label: '已入库', color: 'geekblue' },
    withdrawn: { label: '已撤回', color: 'default' },
  }

const CORRECT_LABELS: Record<string, string> = {
  A: 'A',
  B: 'B',
  C: 'C',
  D: 'D',
}

interface ReviewActionMeta {
  value: ReviewOutcome
  label: string
  desc: string
  danger?: boolean
  minNote: number
  confirm: string
  success: string
}

const REVIEW_ACTIONS: ReviewActionMeta[] = [
  {
    value: 'accepted',
    label: '采纳',
    desc: '标记为已采纳，等待 migration 发布入库；备注可选',
    minNote: 0,
    confirm: '将该投稿标记为「已采纳」，等待 migration 发布入库。继续？',
    success: '已采纳，等待 migration 发布入库',
  },
  {
    value: 'needs_changes',
    label: '退回修改',
    desc: '作者修改后会重新进入待审队列；备注必填 ≥5 字，会通知作者',
    minNote: 5,
    confirm: '将投稿退回给作者修改，作者改完会重新进入待审队列。继续？',
    success: '已退回修改，作者会收到通知',
  },
  {
    value: 'rejected',
    label: '未采纳',
    desc: '终态，不进入题库；备注必填 ≥5 字，会通知作者',
    danger: true,
    minNote: 5,
    confirm: '将该投稿标记为「未采纳」（终态，作者不可再修改重提）。继续？',
    success: '已标记未采纳',
  },
]

/* ── helpers ────────────────────────────────────────────────── */

const PAGE_SIZE = 20

function normalizeOptions(raw: unknown): ExamOption[] {
  if (!Array.isArray(raw)) return []
  return raw
    .filter(
      (o): o is ExamOption =>
        !!o &&
        typeof o === 'object' &&
        typeof (o as ExamOption).key === 'string' &&
        typeof (o as ExamOption).text === 'string',
    )
    .sort((a, b) => a.key.localeCompare(b.key))
}

function validateEdit(f: EditContent): string | null {
  const bodyLen = f.body.trim().length
  if (bodyLen < 5 || bodyLen > 500) {
    return `题干需 5-500 字（当前 ${bodyLen}）`
  }
  const expLen = f.explanation.trim().length
  if (expLen < 1 || expLen > 2000) {
    return `解析需 1-2000 字（当前 ${expLen}）`
  }
  if (!CORRECT_LABELS[f.correct_option]) return '请选择正确答案'
  const texts = f.options.map((o) => o.text.trim().toLowerCase())
  if (texts.some((t) => t.length < 1 || t.length > 200)) {
    return '每个选项需 1-200 字'
  }
  if (new Set(texts).size < texts.length) return '四个选项内容不能重复'
  return null
}

function shortId(id: string): string {
  return id.slice(0, 8)
}

/* ── 题目内容渲染（预览 / 修订历史共用） ─────────────────────── */

function QuestionBlock({
  body,
  options,
  correct,
  explanation,
}: {
  body: string
  options: ExamOption[]
  correct: string
  explanation: string
}) {
  return (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      <Typography.Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
        {body}
      </Typography.Paragraph>
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        {options.map((o) => {
          const isCorrect = o.key === correct
          return (
            <Space
              key={o.key}
              size={8}
              align="start"
              className="exam-question-option"
            >
              <Tag
                color={isCorrect ? 'success' : 'default'}
                style={{ marginRight: 0, minWidth: 24, textAlign: 'center' }}
              >
                {o.key}
              </Tag>
              <Typography.Text strong={isCorrect}>{o.text}</Typography.Text>
              {isCorrect ? (
                <Tag color="success" style={{ marginRight: 0 }}>
                  正确
                </Tag>
              ) : null}
            </Space>
          )
        })}
      </Space>
      <div>
        <Typography.Text type="secondary">解析：</Typography.Text>
        <Typography.Paragraph
          style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}
        >
          {explanation}
        </Typography.Paragraph>
      </div>
    </Space>
  )
}

/* ── 主组件 ─────────────────────────────────────────────────── */

function ExamContributionsPage() {
  const isMobile = useIsMobile()
  const [tab, setTab] = useState<SubmissionStatus>('pending')
  const [page, setPage] = useState(1)
  const [target, setTarget] = useState<string | null>(null)

  useEffect(() => {
    setPage(1)
  }, [tab])

  const listQuery = useQuery({
    queryKey: ['exam_submissions', { tab, page }],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('list_exam_question_submissions', {
          p_status: tab,
          p_limit: PAGE_SIZE,
          p_offset: (page - 1) * PAGE_SIZE,
        })
      if (error) throw error
      const r = data as {
        ok: boolean
        items?: ExamSubmissionRow[]
        total?: number
        error?: string
      }
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return { rows: r.items ?? [], total: r.total }
    },
  })

  const rows = listQuery.data?.rows ?? []
  // 优先用 RPC 返回的 total；旧版 RPC 无 total 时按"整页满即可能有下一页"估算
  const hasMore = rows.length === PAGE_SIZE
  const estimatedTotal =
    listQuery.data?.total ??
    (hasMore ? (page + 1) * PAGE_SIZE : (page - 1) * PAGE_SIZE + rows.length)

  const columns: ColumnsType<ExamSubmissionRow> = useMemo(
    () => [
      {
        title: '题目',
        render: (_: unknown, row) => (
          <Space size={2} direction="vertical" style={{ rowGap: 0 }}>
            <Typography.Paragraph
              ellipsis={{ rows: 2 }}
              style={{ marginBottom: 0, maxWidth: 480 }}
            >
              {row.body}
            </Typography.Paragraph>
            <Space size={6} wrap>
              <Typography.Text
                type="secondary"
                style={{ fontSize: 11 }}
                copyable={{ text: row.id }}
              >
                #{shortId(row.id)}
              </Typography.Text>
              <Tag
                bordered={false}
                color="blue"
                style={{ fontSize: 11, marginRight: 0 }}
              >
                单选
              </Tag>
              <Tag
                bordered={false}
                color="blue"
                style={{ fontSize: 11, marginRight: 0 }}
              >
                答案 {CORRECT_LABELS[row.correct_option] ?? row.correct_option}
              </Tag>
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                v{row.version}
              </Typography.Text>
            </Space>
          </Space>
        ),
      },
      {
        title: '投稿人',
        width: 150,
        render: (_: unknown, row) => (
          <Space direction="vertical" size={0}>
            <Space size={4} wrap>
              <Typography.Text style={{ fontSize: 13 }}>
                {row.submitter_display || '已注销'}
              </Typography.Text>
              {row.show_contributor ? (
                <Tag
                  bordered={false}
                  color="cyan"
                  style={{ fontSize: 11, marginRight: 0 }}
                >
                  署名
                </Tag>
              ) : null}
            </Space>
            {row.submitter_username ? (
              <Typography.Text
                type="secondary"
                style={{ fontSize: 11 }}
                copyable={{ text: row.user_id ?? '' }}
              >
                @{row.submitter_username}
              </Typography.Text>
            ) : row.user_id ? (
              <Typography.Text
                type="secondary"
                style={{
                  fontSize: 11,
                  fontFamily: 'var(--ant-font-family-code, monospace)',
                }}
                copyable={{ text: row.user_id }}
              >
                {shortId(row.user_id)}…
              </Typography.Text>
            ) : (
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                已注销
              </Typography.Text>
            )}
          </Space>
        ),
      },
      {
        title: '状态',
        width: 90,
        render: (_: unknown, row) => {
          const meta = STATUS_META[row.status]
          return <Tag color={meta.color}>{meta.label}</Tag>
        },
      },
      {
        title: '提交时间',
        width: 130,
        render: (_: unknown, row) => (
          <Typography.Text
            style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
          >
            {formatCST(row.created_at, 'MM-DD HH:mm')}
          </Typography.Text>
        ),
      },
      {
        title: '审核记录',
        width: 160,
        render: (_: unknown, row) =>
          row.reviewed_at ? (
            <Space direction="vertical" size={0}>
              <Typography.Text style={{ fontSize: 13 }}>
                {row.reviewer_display || row.reviewed_by}
              </Typography.Text>
              <Typography.Text
                type="secondary"
                style={{ fontSize: 11, fontVariantNumeric: 'tabular-nums' }}
              >
                {formatCST(row.reviewed_at, 'MM-DD HH:mm')}
              </Typography.Text>
            </Space>
          ) : (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              未审
            </Typography.Text>
          ),
      },
      {
        title: '操作',
        width: 90,
        fixed: 'right',
        render: (_: unknown, row) => (
          <Button
            type="link"
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              setTarget(row.id)
            }}
          >
            {row.status === 'pending' || row.status === 'accepted'
              ? '审核'
              : '查看'}
          </Button>
        ),
      },
    ],
    [],
  )

  return (
    <>
      <Card
        title="试题投稿"
        extra={
          <Tooltip title={isMobile ? '刷新' : undefined}>
            <Button
              icon={<ReloadOutlined />}
              aria-label="刷新"
              onClick={() => listQuery.refetch()}
              loading={listQuery.isFetching && !listQuery.isLoading}
            >
              {isMobile ? null : '刷新'}
            </Button>
          </Tooltip>
        }
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <div className="admin-scroll-rail moderation-tab-rail">
            <Segmented<SubmissionStatus>
              value={tab}
              onChange={(v) => setTab(v)}
              options={(Object.keys(STATUS_META) as SubmissionStatus[]).map(
                (s) => ({ label: STATUS_META[s].label, value: s }),
              )}
            />
          </div>

          {tab === 'accepted' ? (
            <Alert
              type="info"
              showIcon
              message="已采纳的试题由 backend 数据 migration 调用 private.publish_exam_question_submission 发布入题库，本面板不做发布操作。"
            />
          ) : null}

          <div className="admin-table-wrap">
            <Table<ExamSubmissionRow>
              rowKey="id"
              size="middle"
              loading={listQuery.isLoading}
              columns={columns}
              dataSource={rows}
              scroll={{ x: 1000 }}
              pagination={{
                current: page,
                pageSize: PAGE_SIZE,
                total: estimatedTotal,
                showSizeChanger: false,
                onChange: (p) => setPage(p),
              }}
            />
          </div>

          <AdminMobileList
            rows={rows}
            rowKey={(row) => row.id}
            loading={listQuery.isLoading || listQuery.isFetching}
            page={page}
            pageSize={PAGE_SIZE}
            total={estimatedTotal}
            onPageChange={setPage}
            renderItem={(row) => (
              <article
                className="admin-mobile-record"
                role="button"
                tabIndex={0}
                onClick={() => setTarget(row.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setTarget(row.id)
                }}
              >
                <div className="admin-mobile-record__head">
                  <div className="admin-mobile-record__title">
                    <Typography.Paragraph ellipsis={{ rows: 2 }}>
                      {row.body}
                    </Typography.Paragraph>
                  </div>
                  <Tag color={STATUS_META[row.status].color}>
                    {STATUS_META[row.status].label}
                  </Tag>
                </div>
                <div className="admin-mobile-record__badges">
                  <Tag>单选</Tag>
                  <Tag>
                    答案{' '}
                    {CORRECT_LABELS[row.correct_option] ?? row.correct_option}
                  </Tag>
                  <Tag>v{row.version}</Tag>
                  {row.show_contributor ? <Tag color="cyan">署名</Tag> : null}
                </div>
                <div className="admin-mobile-record__meta">
                  <span>#{shortId(row.id)}</span>
                  <span>投稿人 {row.submitter_display || '已注销'}</span>
                  <span>{formatCST(row.created_at, 'MM-DD HH:mm')}</span>
                  {row.reviewed_at && row.reviewer_display ? (
                    <span>审核 {row.reviewer_display}</span>
                  ) : null}
                </div>
                {row.status === 'pending' || row.status === 'accepted' ? (
                  <AdminRecordActions
                    actions={[
                      {
                        key: 'review',
                        label: '审核',
                        onClick: () => setTarget(row.id),
                      },
                    ]}
                  />
                ) : null}
              </article>
            )}
          />
        </Space>
      </Card>

      <ReviewDrawer
        target={target}
        onClose={() => setTarget(null)}
        onOpenSubmission={setTarget}
      />
    </>
  )
}

/* ── 审核抽屉：详情 + 题目 + 审核面板 + 修订历史 ────────────── */

function ReviewDrawer({
  target,
  onClose,
  onOpenSubmission,
}: {
  target: string | null
  onClose: () => void
  /** 打开另一条投稿（查重面板里的相似投稿） */
  onOpenSubmission: (id: string) => void
}) {
  const qc = useQueryClient()
  const { message, modal } = AntdApp.useApp()
  const isMobile = useIsMobile()

  const [note, setNote] = useState('')
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<EditContent | null>(null)
  const [pendingAction, setPendingAction] = useState<ReviewOutcome | null>(null)

  useEffect(() => {
    setNote('')
    setEditing(false)
    setForm(null)
    setPendingAction(null)
  }, [target])

  const detailQuery = useQuery({
    queryKey: ['exam_submission', target],
    enabled: !!target,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('get_exam_question_submission', { p_id: target })
      if (error) throw error
      const r = data as DetailResult
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r
    },
  })

  const sub = detailQuery.data?.submission
  const revisions = detailQuery.data?.revisions ?? []
  const dedup = useMemo(
    () => parseExamDedup(detailQuery.data?.dedup),
    [detailQuery.data?.dedup],
  )
  const reviewable = sub
    ? sub.status === 'pending' || sub.status === 'accepted'
    : false
  const options = useMemo(
    () => (sub ? normalizeOptions(sub.options) : []),
    [sub],
  )

  const startEditing = () => {
    if (!sub) return
    setForm({
      body: sub.body,
      options: normalizeOptions(sub.options),
      correct_option: sub.correct_option,
      explanation: sub.explanation,
    })
    setEditing(true)
  }

  const reviewMutation = useMutation({
    mutationFn: async (params: {
      meta: ReviewActionMeta
      content: EditContent | null
    }) => {
      if (!sub) throw new Error('no_target')
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('review_exam_question_submission', {
          p_id: sub.id,
          p_expected_version: sub.version,
          p_status: params.meta.value,
          p_review_note: note.trim() || null,
          p_content: params.content,
        })
      if (error) throw error
      const r = data as { ok: boolean; error?: string }
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r
    },
    onSuccess: (_r, params) => {
      message.success(params.meta.success)
      qc.invalidateQueries({ queryKey: ['exam_submissions'] })
      qc.invalidateQueries({ queryKey: ['exam_submission'] })
      onClose()
    },
    onError: (e: Error) => {
      if (e.message === 'conflict') {
        message.error('版本已变化（可能已被处理或作者已改稿），请刷新后重试')
      } else if (e.message === 'invalid_state') {
        message.error('当前状态不允许该操作，请刷新后重试')
      } else if (e.message === 'invalid_request') {
        message.error(
          '提交内容未通过校验（题干 5-500 字、四个选项 1-200 字且不重复、解析 1-2000 字）',
        )
      } else if (e.message === 'forbidden') {
        message.error('无权限（需要管理员）')
      } else if (e.message === 'not_found') {
        message.error('投稿不存在或已被清理')
      } else {
        message.error(e.message || '操作失败')
      }
    },
  })

  const handleAction = (meta: ReviewActionMeta) => {
    if (!sub) return
    const trimmed = note.trim()
    if (trimmed.length < meta.minNote) {
      message.warning(`该动作必须填写不少于 ${meta.minNote} 字的审核备注`)
      return
    }
    if (editing && form) {
      const invalid = validateEdit(form)
      if (invalid) {
        message.warning(invalid)
        return
      }
    }
    setPendingAction(meta.value)
    modal.confirm({
      title: `确认「${meta.label}」？`,
      content:
        editing && form
          ? `${meta.confirm}将同时保存修改后的题目内容。`
          : meta.confirm,
      okText: '执行',
      cancelText: '取消',
      okButtonProps: { danger: meta.danger },
      onOk: () =>
        reviewMutation.mutate({
          meta,
          content: editing && form ? form : null,
        }),
      onCancel: () => setPendingAction(null),
      afterClose: () => setPendingAction(null),
    })
  }

  return (
    <Drawer
      open={!!target}
      onClose={onClose}
      width={isMobile ? '100%' : 720}
      className="exam-review-drawer"
      title={target ? `试题投稿 #${shortId(target)}` : ''}
      destroyOnHidden
    >
      {!target ? null : detailQuery.isLoading ? (
        <Typography.Text type="secondary">加载中…</Typography.Text>
      ) : sub ? (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Descriptions
            column={1}
            size="small"
            bordered
            items={[
              {
                label: '状态',
                children: (
                  <Tag color={STATUS_META[sub.status].color}>
                    {STATUS_META[sub.status].label}
                  </Tag>
                ),
              },
              {
                label: '投稿人',
                children: (
                  <Space direction="vertical" size={0}>
                    <span>{sub.submitter_display || '已注销'}</span>
                    {sub.submitter_username ? (
                      <Typography.Text
                        type="secondary"
                        style={{ fontSize: 12 }}
                      >
                        @{sub.submitter_username}
                      </Typography.Text>
                    ) : null}
                    {sub.user_id ? (
                      <Typography.Text
                        type="secondary"
                        copyable={{ text: sub.user_id }}
                        style={{
                          fontSize: 11,
                          fontFamily: 'var(--ant-font-family-code, monospace)',
                        }}
                      >
                        {sub.user_id}
                      </Typography.Text>
                    ) : null}
                  </Space>
                ),
              },
              {
                label: '署名',
                children: sub.show_contributor ? (
                  <Tag color="cyan" style={{ marginRight: 0 }}>
                    同意展示贡献者
                  </Tag>
                ) : (
                  <Typography.Text type="secondary">不展示</Typography.Text>
                ),
              },
              {
                label: '提交时间',
                children: (
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatCST(sub.created_at)} · v{sub.version}
                  </span>
                ),
              },
              {
                label: '投稿备注',
                children: sub.submitter_note ? (
                  <Typography.Paragraph
                    style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}
                  >
                    {sub.submitter_note}
                  </Typography.Paragraph>
                ) : (
                  <Typography.Text type="secondary">—</Typography.Text>
                ),
              },
              ...(sub.reviewed_at
                ? [
                    {
                      label: '审核记录',
                      children: (
                        <Space direction="vertical" size={0}>
                          <span>
                            {sub.reviewer_display || sub.reviewed_by} ·{' '}
                            {formatCST(sub.reviewed_at)}
                          </span>
                          {sub.review_note ? (
                            <Typography.Paragraph
                              style={{
                                marginBottom: 0,
                                whiteSpace: 'pre-wrap',
                              }}
                            >
                              {sub.review_note}
                            </Typography.Paragraph>
                          ) : null}
                        </Space>
                      ),
                    },
                  ]
                : []),
              ...(sub.exam_question_id != null
                ? [
                    {
                      label: '入库信息',
                      children: (
                        <span>
                          题目 ID {sub.exam_question_id} ·{' '}
                          {formatCST(sub.published_at)}
                        </span>
                      ),
                    },
                  ]
                : []),
            ]}
          />

          <div>
            <Typography.Title level={5} style={{ marginBottom: 8 }}>
              题目内容（当前 v{sub.version}）
            </Typography.Title>
            <QuestionBlock
              body={sub.body}
              options={options}
              correct={sub.correct_option}
              explanation={sub.explanation}
            />
          </div>

          <ExamDedupPanel dedup={dedup} onOpenSubmission={onOpenSubmission} />

          {reviewable ? (
            <div>
              <Typography.Title level={5} style={{ marginBottom: 8 }}>
                审核面板
              </Typography.Title>
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Space size={8} align="start">
                  <Switch
                    checked={editing}
                    onChange={(v) => (v ? startEditing() : setEditing(false))}
                  />
                  <Typography.Text>
                    修改内容（采纳 /
                    退回时可整体替换题干、选项、答案、解析；不影响作者备注）
                  </Typography.Text>
                </Space>

                {editing && form ? (
                  <Space
                    direction="vertical"
                    size={10}
                    style={{ width: '100%' }}
                  >
                    <Input.TextArea
                      value={form.body}
                      onChange={(e) =>
                        setForm({ ...form, body: e.target.value })
                      }
                      autoSize={{ minRows: 2, maxRows: 6 }}
                      maxLength={500}
                      showCount
                      placeholder="题干（5-500 字）"
                    />
                    {form.options.map((o, i) => (
                      <Input
                        key={o.key}
                        addonBefore={o.key}
                        value={o.text}
                        onChange={(e) => {
                          const next = [...form.options]
                          next[i] = { ...o, text: e.target.value }
                          setForm({ ...form, options: next })
                        }}
                        maxLength={200}
                        placeholder={`选项 ${o.key}（1-200 字，互相不重复）`}
                      />
                    ))}
                    <Space size={8} wrap>
                      <Typography.Text>正确答案</Typography.Text>
                      <Radio.Group
                        value={form.correct_option}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            correct_option: e.target.value as string,
                          })
                        }
                        optionType="button"
                        buttonStyle="solid"
                        options={['A', 'B', 'C', 'D'].map((k) => ({
                          value: k,
                          label: k,
                        }))}
                      />
                    </Space>
                    <Input.TextArea
                      value={form.explanation}
                      onChange={(e) =>
                        setForm({ ...form, explanation: e.target.value })
                      }
                      autoSize={{ minRows: 2, maxRows: 6 }}
                      maxLength={2000}
                      showCount
                      placeholder="解析（1-2000 字）"
                    />
                  </Space>
                ) : null}

                <ReplyTemplatePicker
                  context="exam_submission"
                  actions={REVIEW_ACTIONS.map((a) => a.value)}
                  value={note}
                  onChange={setNote}
                  maxLength={1000}
                  disabled={reviewMutation.isPending}
                />
                <Input.TextArea
                  placeholder="审核备注（退回 / 未采纳必填 ≥5 字，会通知作者；采纳可选，写入审计日志）"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  maxLength={1000}
                  showCount
                />
                <Space wrap className="exam-review-actions">
                  {REVIEW_ACTIONS.map((a) => (
                    <Button
                      key={a.value}
                      danger={a.danger}
                      loading={
                        reviewMutation.isPending && pendingAction === a.value
                      }
                      disabled={
                        reviewMutation.isPending && pendingAction !== a.value
                      }
                      onClick={() => handleAction(a)}
                    >
                      {a.label}
                    </Button>
                  ))}
                </Space>
              </Space>
            </div>
          ) : null}

          {revisions.length > 1 ? (
            <div>
              <Typography.Title level={5} style={{ marginBottom: 8 }}>
                修订历史（{revisions.length}）
              </Typography.Title>
              <Collapse
                items={revisions.map((rev) => ({
                  key: String(rev.version),
                  label: (
                    <Space size={[8, 4]} wrap>
                      <Typography.Text strong>v{rev.version}</Typography.Text>
                      <Typography.Text
                        type="secondary"
                        style={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {formatCST(rev.created_at, 'YYYY-MM-DD HH:mm')}
                      </Typography.Text>
                      {rev.snapshot?.status ? (
                        <Tag color={STATUS_META[rev.snapshot.status].color}>
                          {STATUS_META[rev.snapshot.status].label}
                        </Tag>
                      ) : null}
                    </Space>
                  ),
                  children: (
                    <Space
                      direction="vertical"
                      size={8}
                      style={{ width: '100%' }}
                    >
                      {rev.snapshot?.review_note ? (
                        <Typography.Paragraph
                          type="warning"
                          style={{ marginBottom: 0 }}
                        >
                          审核备注：{rev.snapshot.review_note}
                        </Typography.Paragraph>
                      ) : null}
                      <QuestionBlock
                        body={rev.snapshot?.body ?? '—'}
                        options={normalizeOptions(rev.snapshot?.options)}
                        correct={rev.snapshot?.correct_option ?? ''}
                        explanation={rev.snapshot?.explanation ?? ''}
                      />
                    </Space>
                  ),
                }))}
              />
            </div>
          ) : null}
        </Space>
      ) : (
        <Alert type="error" message="加载失败" />
      )}
    </Drawer>
  )
}

/* ── 查重：提交时标记 + 实时相似（admin.get_exam_question_submission.dedup） ── */

function ExamDedupPanel({
  dedup,
  onOpenSubmission,
}: {
  dedup: ExamDedup
  onOpenSubmission: (id: string) => void
}) {
  const { flag, similar } = dedup
  const meta = flag ? EXAM_DECISION_META[flag.decision] : null

  return (
    <div className="exam-dedup-panel">
      <Typography.Title level={5} style={{ marginBottom: 8 }}>
        查重
      </Typography.Title>
      <Space direction="vertical" size={10} style={{ width: '100%' }}>
        {flag && meta ? (
          <Alert
            type={meta.alert}
            showIcon
            message={
              <Space size={[6, 4]} wrap>
                <span>提交时标记：{meta.label}</span>
                <Tag color={meta.tag} style={{ marginInlineEnd: 0 }}>
                  {flag.decision}
                </Tag>
                {flag.bankMatch ? (
                  <Tag color="red" style={{ marginInlineEnd: 0 }}>
                    命中题库 / 他人投稿
                  </Tag>
                ) : (
                  <Tag style={{ marginInlineEnd: 0 }}>仅本人相似</Tag>
                )}
              </Space>
            }
            description={
              <Space direction="vertical" size={4}>
                <span>{meta.desc}</span>
                {flag.top.length > 0 ? (
                  <Space size={[4, 4]} wrap>
                    {flag.top.map((t) => (
                      <Tooltip
                        key={t.refKey}
                        title={similarityBreakdown(t.sem, t.lex)}
                      >
                        <Tag
                          color={similarityColor(t.similarity)}
                          style={{
                            marginInlineEnd: 0,
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {refKeyLabel(t.refKey)}
                          {t.own ? ' · 本人' : ''} ·{' '}
                          {formatSimilarity(t.similarity)}
                        </Tag>
                      </Tooltip>
                    ))}
                  </Space>
                ) : null}
              </Space>
            }
          />
        ) : (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            提交时无查重标记
          </Typography.Text>
        )}

        {similar.length === 0 ? (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            暂无相似题目（相似度 ≥ 0.50）
          </Typography.Text>
        ) : (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {similar.map((item) => (
              <div
                key={`${item.kind}:${item.id}`}
                className="exam-dedup-similar"
              >
                <Space size={[6, 4]} wrap>
                  <Tooltip title={similarityBreakdown(item.sem, item.lex)}>
                    <Tag
                      color={similarityColor(item.similarity)}
                      style={{
                        marginInlineEnd: 0,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      相似度 {formatSimilarity(item.similarity)}
                    </Tag>
                  </Tooltip>
                  {item.kind === 'bank' ? (
                    <Tag color="geekblue" style={{ marginInlineEnd: 0 }}>
                      题库 #{item.id}
                    </Tag>
                  ) : (
                    <>
                      <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                        投稿 #{shortId(item.id)}
                      </Tag>
                      {item.status && item.status in STATUS_META ? (
                        <Tag
                          color={
                            STATUS_META[item.status as SubmissionStatus].color
                          }
                          style={{ marginInlineEnd: 0 }}
                        >
                          {STATUS_META[item.status as SubmissionStatus].label}
                        </Tag>
                      ) : null}
                    </>
                  )}
                  {item.own ? (
                    <Tag color="cyan" style={{ marginInlineEnd: 0 }}>
                      本人
                    </Tag>
                  ) : null}
                  {item.kind === 'submission' ? (
                    <Button
                      type="link"
                      size="small"
                      style={{ padding: 0 }}
                      onClick={() => onOpenSubmission(item.id)}
                    >
                      查看投稿
                    </Button>
                  ) : null}
                </Space>
                {item.body ? (
                  <Typography.Paragraph
                    type="secondary"
                    style={{
                      marginBottom: 0,
                      marginTop: 4,
                      fontSize: 13,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {item.body}
                    {item.body.length >= 120 ? '…' : ''}
                  </Typography.Paragraph>
                ) : null}
              </div>
            ))}
          </Space>
        )}
      </Space>
    </div>
  )
}
